import React, { useState, useEffect, useCallback, useRef } from 'react';

const AudioVolumePanel = ({ selectedBlock, transcript, onTranscriptUpdate, onSave, audioPlayerRefs }) => {
    const [volume, setVolume] = useState(1.0);
    const [isDragging, setIsDragging] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [dimensions, setDimensions] = useState({ height: 200 });
    const sliderRef = useRef(null);
    const containerRef = useRef(null);
    const inputRef = useRef(null);

    // Observer pour la taille du container
    useEffect(() => {
        const resizeObserver = new ResizeObserver((entries) => {
            for (let entry of entries) {
                const { height } = entry.contentRect;
                // Ajuster la hauteur du slider en fonction du container
                setDimensions({ height: Math.max(150, Math.min(400, height - 60)) });
            }
        });

        if (containerRef.current) {
            resizeObserver.observe(containerRef.current);
        }

        return () => {
            resizeObserver.disconnect();
        };
    }, []);

    useEffect(() => {
        setVolume(selectedBlock?.audio_gain ?? 1.0);
    }, [selectedBlock]);

    const updateDirectAudioVolume = useCallback((newVolume) => {
        if (!selectedBlock || !audioPlayerRefs?.current) return;
        const playerRef = audioPlayerRefs.current[selectedBlock.speaker];
        if (playerRef?.updateVolume) {
            playerRef.updateVolume({ ...selectedBlock, audio_gain: newVolume });
        }
    }, [selectedBlock, audioPlayerRefs]);

    const handleVolumeChange = useCallback((newVolume) => {
        // Limiter entre 0 et 1 (0% à 100%)
        const clampedVolume = Math.max(0, Math.min(1, newVolume));
        setVolume(clampedVolume);
        updateDirectAudioVolume(clampedVolume);

        if (selectedBlock) {
            const updatedTranscript = transcript.map(segment =>
                (segment.start === selectedBlock.start && segment.speaker === selectedBlock.speaker)
                    ? { ...segment, audio_gain: clampedVolume }
                    : segment
            );
            onTranscriptUpdate(updatedTranscript);
        }
    }, [selectedBlock, transcript, onTranscriptUpdate, updateDirectAudioVolume]);

    const handleMouseDown = (e) => {
        setIsDragging(true);
        handleMouseMove(e);
    };

    const handleMouseMove = useCallback((e) => {
        if (!isDragging && e.type === 'mousemove') return;
        if (!sliderRef.current) return;

        const rect = sliderRef.current.getBoundingClientRect();
        const y = e.clientY - rect.top;
        const percentage = 1 - (y / rect.height);
        const newVolume = percentage; // 0 à 1 (0% à 100%)

        handleVolumeChange(newVolume);
    }, [isDragging, handleVolumeChange]);

    const handleMouseUp = useCallback(() => {
        if (isDragging) {
            setIsDragging(false);
            onSave?.();
        }
    }, [isDragging, onSave]);

    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [isDragging, handleMouseMove, handleMouseUp]);

    if (!selectedBlock) {
        return (
            <div style={styles.emptyContainer}>
                <p style={styles.emptyText}>Sélectionnez un segment audio.</p>
            </div>
        );
    }

    if (!selectedBlock) {
        return (
            <div style={styles.emptyContainer}>
                <p style={styles.emptyText}>Sélectionnez un segment audio.</p>
            </div>
        );
    }

    const volumePercent = Math.round(volume * 100);
    const thumbPosition = volume * 100; // Position en %

    // Marques pour l'échelle - ajustées dynamiquement
    const markCount = dimensions.height > 250 ? 5 : 3;
    const marks = markCount === 5
        ? [
            { value: 0, label: '0' },
            { value: 0.25, label: '25' },
            { value: 0.5, label: '50' },
            { value: 0.75, label: '75' },
            { value: 1.0, label: '100' },
        ]
        : [
            { value: 0, label: '0' },
            { value: 0.5, label: '50' },
            { value: 1.0, label: '100' },
        ];

    const handleValueClick = () => {
        setIsEditing(true);
        setInputValue(volumePercent.toString());
        setTimeout(() => {
            inputRef.current?.select();
        }, 0);
    };

    const handleInputChange = (e) => {
        const value = e.target.value.replace(/[^\d]/g, '');
        if (value === '' || (parseInt(value) >= 0 && parseInt(value) <= 100)) {
            setInputValue(value);
        }
    };

    const handleInputBlur = () => {
        const numValue = parseInt(inputValue) || 0;
        const clampedValue = Math.max(0, Math.min(100, numValue));
        handleVolumeChange(clampedValue / 100);
        setIsEditing(false);
        onSave?.();
    };

    const handleInputKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleInputBlur();
        } else if (e.key === 'Escape') {
            setIsEditing(false);
            setInputValue(volumePercent.toString());
        }
    };

    return (
        <div ref={containerRef} style={styles.container}>
            <div style={styles.mainContent}>
                <div style={{ ...styles.volumeWidget, height: `${dimensions.height}px` }}>
                    {/* Échelle avec les valeurs */}
                    <div style={styles.scaleContainer}>
                        {marks.slice().reverse().map((mark) => (
                            <span key={mark.value} style={styles.scaleLabel}>
                                {mark.label}
                            </span>
                        ))}
                    </div>

                    {/* Container du slider */}
                    <div style={styles.sliderWrapper}>
                        {/* Lignes de graduation */}
                        <div style={styles.graduationsContainer}>
                            {marks.map((mark) => (
                                <div
                                    key={mark.value}
                                    style={{
                                        ...styles.graduation,
                                        bottom: `${mark.value * 100}%`,
                                        width: mark.value === 0.5 ? '25px' : '18px',
                                        backgroundColor: mark.value === 0.5 ? '#666' : '#444',
                                    }}
                                />
                            ))}
                        </div>

                        {/* Rail du slider */}
                        <div
                            ref={sliderRef}
                            style={styles.sliderRail}
                            onMouseDown={handleMouseDown}
                        >
                            {/* Track (partie active) */}
                            <div
                                style={{
                                    ...styles.sliderTrack,
                                    height: `${thumbPosition}%`,
                                }}
                            />

                            {/* Thumb (curseur) */}
                            <div
                                style={{
                                    ...styles.sliderThumb,
                                    bottom: `${thumbPosition}%`,
                                    cursor: isDragging ? 'grabbing' : 'grab',
                                }}
                            >
                                <div style={styles.thumbLine} />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Affichage de la valeur en bas */}
                <div style={styles.bottomValueContainer}>
                    <div style={styles.bottomValueDisplay}>
                        {isEditing ? (
                            <input
                                ref={inputRef}
                                type="text"
                                value={inputValue}
                                onChange={handleInputChange}
                                onBlur={handleInputBlur}
                                onKeyDown={handleInputKeyDown}
                                style={styles.valueInput}
                                maxLength="3"
                            />
                        ) : (
                            <div style={styles.valueBox} onClick={handleValueClick}>
                                {volumePercent}
                            </div>
                        )}
                        <span style={styles.percentSymbol}>%</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

const styles = {
    container: {
        height: '100%',
        backgroundColor: '#2D2D2D',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '10px',
        minHeight: '180px',
    },
    emptyContainer: {
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '10px',
        backgroundColor: '#2D2D2D',
        minHeight: '180px',
    },
    emptyText: {
        color: 'rgba(255,255,255,0.6)',
        fontSize: '12px',
        textAlign: 'center',
    },
    mainContent: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '12px',
        height: '100%',
        width: '100%',
        justifyContent: 'center',
    },
    volumeWidget: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        justifyContent: 'center',
    },
    scaleContainer: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        height: '100%',
        marginRight: '4px',
        flexShrink: 0,
    },
    scaleLabel: {
        color: '#888',
        fontSize: '10px',
        fontFamily: 'monospace',
        userSelect: 'none',
        lineHeight: 1,
        whiteSpace: 'nowrap',
        textAlign: 'right',
        width: '20px',
    },
    sliderWrapper: {
        position: 'relative',
        height: '100%',
        width: '40px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    graduationsContainer: {
        position: 'absolute',
        height: '100%',
        width: '100%',
        pointerEvents: 'none',
    },
    graduation: {
        position: 'absolute',
        left: '50%',
        transform: 'translateX(-50%)',
        height: '1px',
    },
    sliderRail: {
        position: 'relative',
        width: '6px',
        height: '100%',
        backgroundColor: '#2a2a2a',
        border: '1px solid #444',
        cursor: 'pointer',
    },
    sliderTrack: {
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: '#6366f1',
    },
    sliderThumb: {
        position: 'absolute',
        left: '50%',
        transform: 'translate(-50%, 50%)',
        width: '20px',
        height: '10px',
        borderRadius: '2px',
        backgroundColor: '#e0e0e0',
        border: '1px solid #999',
        boxShadow: '0 1px 3px rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    thumbLine: {
        width: '2px',
        height: '5px',
        backgroundColor: '#666',
    },
    bottomValueContainer: {
        position: 'relative',
        display: 'flex',
        justifyContent: 'center',
    },
    bottomValueDisplay: {
        display: 'flex',
        alignItems: 'center',
        gap: '2px',
    },
    valueBox: {
        padding: '4px 8px',
        backgroundColor: '#2a2a2a',
        border: '1px solid #444',
        borderRadius: '2px',
        color: '#fff',
        fontSize: '12px',
        fontFamily: 'monospace',
        minWidth: '40px',
        textAlign: 'center',
        cursor: 'pointer',
        transition: 'background-color 0.2s',
        '&:hover': {
            backgroundColor: '#333',
        },
    },
    valueInput: {
        padding: '4px 8px',
        backgroundColor: '#2a2a2a',
        border: '1px solid #6366f1',
        borderRadius: '2px',
        color: '#fff',
        fontSize: '12px',
        fontFamily: 'monospace',
        minWidth: '40px',
        width: '40px',
        textAlign: 'center',
        outline: 'none',
    },
    percentSymbol: {
        color: '#666',
        fontSize: '11px',
        fontFamily: 'monospace',
    },
};

export default AudioVolumePanel;